import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError, API } from "@/lib/api";
import { fmtCVE, fmtDate, INVOICE_STATUS, DOC_TYPES, PAYMENT_METHODS } from "@/lib/format";
import { NoFiscalNotice } from "@/components/NoFiscalNotice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, FilePdf, Plus, CircleNotch, Trash, CurrencyCircleDollar, WhatsappLogo,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function whatsappLink(inv) {
  const phone = String(inv.client?.phone || "").replace(/\D/g, "");
  const to = phone.length === 7 ? `238${phone}` : phone;
  const msg = `Olá ${inv.client?.name || ""}, segue o ${DOC_TYPES[inv.doc_type] || "documento"} ${inv.number}.\nTotal: ${fmtCVE(inv.totals?.total)}\nPago: ${fmtCVE(inv.amount_paid)}\nEm dívida: ${fmtCVE(inv.balance)}\nObrigado!`;
  return `https://wa.me/${to}?text=${encodeURIComponent(msg)}`;
}

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState({ amount: "", method: "transferencia", date: "", reference: "", notes: "" });

  const load = useCallback(async () => {
    const { data } = await api.get(`/invoices/${id}`);
    setInv(data);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const openPay = () => {
    setPay({ amount: inv?.balance > 0 ? inv.balance : "", method: "transferencia",
      date: new Date().toISOString().slice(0, 10), reference: "", notes: "" });
    setOpen(true);
  };

  const savePay = async () => {
    if (!pay.amount || Number(pay.amount) <= 0) return toast.error("Valor inválido");
    setBusy(true);
    try {
      await api.post("/payments", { invoice_id: id, amount: Number(pay.amount), method: pay.method,
        date: pay.date || null, reference: pay.reference, notes: pay.notes });
      toast.success("Pagamento registado");
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const removePay = async (p) => {
    if (!window.confirm("Eliminar este pagamento?")) return;
    try { await api.delete(`/payments/${p.id}`); toast.success("Pagamento eliminado"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  if (!inv)
    return <div className="flex h-64 items-center justify-center"><CircleNotch size={28} className="animate-spin text-primary" /></div>;

  const st = INVOICE_STATUS[inv.status] || INVOICE_STATUS.rascunho;

  return (
    <div className="space-y-5" data-testid="invoice-detail-page">
      <NoFiscalNotice />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" className="rounded-full" onClick={() => navigate("/vendas")} data-testid="back-btn">
            <ArrowLeft size={18} className="mr-1" /> Voltar
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-display text-2xl font-extrabold tracking-tight">{inv.number}</h1>
              <Badge variant="outline" className={st.cls}>{st.label}</Badge>
            </div>
            <p className="text-sm text-muted-foreground">{DOC_TYPES[inv.doc_type]} · {inv.client?.name}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => window.open(whatsappLink(inv), "_blank")} data-testid="detail-whatsapp-btn">
            <WhatsappLogo size={16} className="mr-1.5" /> WhatsApp
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => window.open(`${API}/invoices/${id}/pdf`, "_blank")} data-testid="detail-pdf-btn">
            <FilePdf size={16} className="mr-1.5" /> PDF
          </Button>
          <Button className="rounded-full" onClick={openPay} data-testid="register-payment-btn">
            <Plus size={16} className="mr-1.5" /> Registar pagamento
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-5 lg:col-span-2">
          <h3 className="mb-3 font-display font-bold">Linhas</h3>
          <div className="space-y-2">
            {inv.items.map((it, i) => {
              const net = Number(it.quantity) * Number(it.unit_price) * (1 - Number(it.discount || 0) / 100);
              return (
                <div key={i} className="flex items-center justify-between border-b border-border/60 pb-2 text-sm last:border-0">
                  <div>
                    <div className="font-medium">{it.description}</div>
                    <div className="text-xs text-muted-foreground">
                      {it.quantity} × {fmtCVE(it.unit_price)} · IVA {it.vat_rate}%{Number(it.discount) > 0 ? ` · desc ${it.discount}%` : ""}
                    </div>
                  </div>
                  <span className="font-medium">{fmtCVE(net)}</span>
                </div>
              );
            })}
          </div>
          <div className="ml-auto mt-4 max-w-[240px] space-y-1 text-sm">
            <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>{fmtCVE(inv.totals?.subtotal)}</span></div>
            <div className="flex justify-between text-muted-foreground"><span>IVA</span><span>{fmtCVE(inv.totals?.vat_total)}</span></div>
            <div className="flex justify-between border-t border-border pt-1 text-base font-bold text-primary"><span>Total</span><span>{fmtCVE(inv.totals?.total)}</span></div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-5">
            <h3 className="mb-3 font-display font-bold">Saldo</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Pago</span><span className="font-medium text-emerald-600">{fmtCVE(inv.amount_paid)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Em dívida</span><span className="font-bold">{fmtCVE(inv.balance)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Vencimento</span><span>{fmtDate(inv.due_date)}</span></div>
            </div>
          </div>
          <div className="rounded-xl border border-border bg-card p-5" data-testid="payments-list">
            <h3 className="mb-3 font-display font-bold">Pagamentos</h3>
            {inv.payments.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sem pagamentos registados.</p>
            ) : (
              <div className="space-y-2">
                {inv.payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between border-b border-border/60 pb-2 text-sm last:border-0">
                    <div>
                      <div className="font-medium text-emerald-600">{fmtCVE(p.amount)}</div>
                      <div className="text-xs text-muted-foreground">{PAYMENT_METHODS[p.method] || p.method} · {fmtDate(p.date)}</div>
                    </div>
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => removePay(p)} data-testid={`delete-payment-${p.id}`}>
                      <Trash size={15} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="payment-dialog">
          <DialogHeader><DialogTitle>Registar pagamento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Valor (CVE) — em dívida: {fmtCVE(inv.balance)}</Label>
              <Input type="number" step="0.01" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} data-testid="payment-amount" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Método</Label>
                <Select value={pay.method} onValueChange={(v) => setPay({ ...pay, method: v })}>
                  <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PAYMENT_METHODS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Data</Label>
                <Input type="date" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} data-testid="payment-date" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Referência / Comprovativo</Label>
              <Input value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} data-testid="payment-reference" />
            </div>
            <div className="space-y-1.5">
              <Label>Notas</Label>
              <Textarea value={pay.notes} onChange={(e) => setPay({ ...pay, notes: e.target.value })} data-testid="payment-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={savePay} disabled={busy} data-testid="save-payment-btn">
              {busy ? <CircleNotch className="animate-spin" /> : <><CurrencyCircleDollar size={16} className="mr-1.5" /> Registar</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
